import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

import { adminApiClient } from "../../services/apiClient";
import { toApiError } from "../../types/api";

import type {
  AnalyticsPeriod,
  LandingFunnelReport,
  LanguageDemandReport,
  CampaignAttributionReport,
  ProductAnalytics,
} from "@lingoshade/domain";

interface AnalyticsState {
  funnel: LandingFunnelReport | null;
  demand: LanguageDemandReport | null;
  funnelError: string | null;
  demandError: string | null;
  funnelRequestId: string | null;
  demandRequestId: string | null;
  funnelFilters: {
    source: string;
    campaign: string;
    content: string;
    device: string;
    country: string;
    excludeAutomated: boolean;
  };
  demandCountry: string;
  report: ProductAnalytics | null;
  attribution: CampaignAttributionReport | null;
  attributionLoading: boolean;
  attributionError: string | null;
  attributionRequestId: string | null;
  countryFilter: string;
  days: AnalyticsPeriod;
  loading: boolean;
  error: string | null;
  requestId: string | null;
}
const initialState: AnalyticsState = {
  funnel: null,
  demand: null,
  funnelError: null,
  demandError: null,
  funnelRequestId: null,
  demandRequestId: null,
  funnelFilters: {
    source: "all",
    campaign: "all",
    content: "all",
    device: "all",
    country: "all",
    excludeAutomated: true,
  },
  demandCountry: "all",
  report: null,
  attribution: null,
  attributionLoading: false,
  attributionError: null,
  attributionRequestId: null,
  countryFilter: "all",
  days: 1,
  loading: false,
  error: null,
  requestId: null,
};

export const fetchProductAnalytics = createAsyncThunk<
  ProductAnalytics,
  AnalyticsPeriod,
  { rejectValue: string }
>(
  "productAnalytics/fetch",
  async (
    days,
    { rejectWithValue, signal },
  ): Promise<ProductAnalytics | ReturnType<typeof rejectWithValue>> => {
    try {
      const response = await adminApiClient.get<ProductAnalytics>(
        "/admin/product-analytics",
        { params: { days }, signal },
      );
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);

export const fetchCampaignAttribution = createAsyncThunk<
  CampaignAttributionReport,
  AnalyticsPeriod,
  { rejectValue: string }
>(
  "productAnalytics/fetchCampaignAttribution",
  async (
    days,
    { rejectWithValue, signal },
  ): Promise<
    CampaignAttributionReport | ReturnType<typeof rejectWithValue>
  > => {
    try {
      const response = await adminApiClient.get<CampaignAttributionReport>(
        "/admin/campaign-attribution",
        { params: { days }, signal },
      );
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);
export const fetchLandingFunnel = createAsyncThunk<
  LandingFunnelReport,
  AnalyticsPeriod,
  { rejectValue: string }
>(
  "productAnalytics/funnel",
  async (
    days,
    { rejectWithValue, signal },
  ): Promise<LandingFunnelReport | ReturnType<typeof rejectWithValue>> => {
    try {
      return (
        await adminApiClient.get<LandingFunnelReport>("/admin/landing-funnel", {
          params: { days },
          signal,
        })
      ).data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);
export const fetchLanguageDemand = createAsyncThunk<
  LanguageDemandReport,
  AnalyticsPeriod,
  { rejectValue: string }
>(
  "productAnalytics/demand",
  async (
    days,
    { rejectWithValue, signal },
  ): Promise<LanguageDemandReport | ReturnType<typeof rejectWithValue>> => {
    try {
      return (
        await adminApiClient.get<LanguageDemandReport>(
          "/admin/language-demand",
          { params: { days }, signal },
        )
      ).data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);
const slice = createSlice({
  name: "productAnalytics",
  initialState,
  reducers: {
    setFunnelFilters: (
      state,
      action: PayloadAction<Partial<AnalyticsState["funnelFilters"]>>,
    ): void => {
      state.funnelFilters = { ...state.funnelFilters, ...action.payload };
    },
    setDemandCountry: (state, action: PayloadAction<string>): void => {
      state.demandCountry = action.payload;
    },
    setAnalyticsPeriod: (
      state,
      action: PayloadAction<AnalyticsPeriod>,
    ): void => {
      state.days = action.payload;
      state.countryFilter = "all";
      state.funnelFilters = initialState.funnelFilters;
      state.demandCountry = "all";
    },
    setAnalyticsCountry: (state, action: PayloadAction<string>): void => {
      state.countryFilter = action.payload;
    },
  },
  extraReducers: (builder): void => {
    builder
      .addCase(fetchLandingFunnel.pending, (state, action): void => {
        state.funnelRequestId = action.meta.requestId;
        state.funnel = null;
        state.funnelError = null;
      })
      .addCase(fetchLandingFunnel.fulfilled, (state, action): void => {
        if (state.funnelRequestId === action.meta.requestId)
          state.funnel = action.payload;
      })
      .addCase(fetchLandingFunnel.rejected, (state, action): void => {
        if (
          state.funnelRequestId === action.meta.requestId &&
          !action.meta.aborted
        )
          state.funnelError = action.payload ?? "REPORT_UNAVAILABLE";
      })
      .addCase(fetchLanguageDemand.pending, (state, action): void => {
        state.demandRequestId = action.meta.requestId;
        state.demand = null;
        state.demandError = null;
      })
      .addCase(fetchLanguageDemand.fulfilled, (state, action): void => {
        if (state.demandRequestId === action.meta.requestId)
          state.demand = action.payload;
      })
      .addCase(fetchLanguageDemand.rejected, (state, action): void => {
        if (
          state.demandRequestId === action.meta.requestId &&
          !action.meta.aborted
        )
          state.demandError = action.payload ?? "REPORT_UNAVAILABLE";
      })
      .addCase(fetchProductAnalytics.pending, (state, action) => {
        state.days = action.meta.arg;
        state.requestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
        state.report = null;
      })
      .addCase(fetchProductAnalytics.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.report = action.payload;
        state.loading = false;
      })
      .addCase(fetchProductAnalytics.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        if (!action.meta.aborted)
          state.error = action.payload ?? "REPORT_UNAVAILABLE";
      })
      .addCase(fetchCampaignAttribution.pending, (state, action): void => {
        state.attributionRequestId = action.meta.requestId;
        state.attributionLoading = true;
        state.attributionError = null;
        state.attribution = null;
      })
      .addCase(fetchCampaignAttribution.fulfilled, (state, action): void => {
        if (state.attributionRequestId !== action.meta.requestId) return;
        const countries = action.payload.countries.filter(
          (row): boolean => row.visits > 0 || row.registrations > 0,
        );
        state.attribution = { ...action.payload, countries };
        state.attributionLoading = false;
        if (
          state.countryFilter !== "all" &&
          !countries.some(
            (row): boolean => (row.country ?? "") === state.countryFilter,
          ) &&
          !(action.payload.recordedRevenue ?? []).some(
            (row): boolean => (row.country ?? "") === state.countryFilter,
          )
        ) {
          state.countryFilter = "all";
        }
      })
      .addCase(fetchCampaignAttribution.rejected, (state, action): void => {
        if (state.attributionRequestId !== action.meta.requestId) return;
        state.attributionLoading = false;
        if (!action.meta.aborted)
          state.attributionError = action.payload ?? "REPORT_UNAVAILABLE";
      })
      .addMatcher(
        (action): boolean => action.type === "adminAuth/adminLogout",
        (): AnalyticsState => initialState,
      );
  },
});
export const {
  setAnalyticsPeriod,
  setAnalyticsCountry,
  setFunnelFilters,
  setDemandCountry,
} = slice.actions;
export default slice.reducer;
